/**
 * Shared duration display formatting.
 *
 * A duration reads as "Xh Ym" everywhere in the portal ("1h 30m", "45m", "2h"), never as
 * decimal hours or "01:30". The rule started life in `reports2/report-formatting.js` and was
 * copied into `module.timesheets/edit-timesheet2/timesheet2-formatting.js`; the Approvals
 * workspace would have been a third copy, so it lives here and both of those re-export it
 * under their existing names. Area modules should import or re-export these rather than
 * copying them.
 *
 * Minutes are the display unit. Where the API supplies seconds, accumulate in seconds and
 * round once, here, at the point of display: rounding each block to a minute and then adding
 * the minutes up loses up to 30 seconds per block (see `timesheet2-formatting.js`).
 *
 * Never throws and never renders "NaN": a missing or non-numeric value is "0m".
 *
 * The legacy `helpers/timeHelpers.js` is deliberately left alone.
 */


/**
 * Format a number of minutes as "1h 30m" / "45m" / "2h" / "0m". Hours get thousands
 * separators (a report can total well over 1,000h).
 */
export function formatMinutes(minutes)
{
	const total = Math.round(minutes || 0);
	if (total <= 0) return "0m";

	const hours = Math.floor(total / 60);
	const mins = total % 60;

	if (hours > 0 && mins > 0) return hours.toLocaleString() + "h " + mins + "m";
	if (hours > 0) return hours.toLocaleString() + "h";
	return mins + "m";
}




/**
 * Round a number of seconds to whole minutes (nearest, half up). Matches the API's group-level
 * rounding (`Math.Round(TotalSeconds / 60)`).
 */
export function roundSecondsToMinutes(seconds)
{
	return Math.round((seconds || 0) / 60);
}




/**
 * Format a number of seconds as a duration, rounded to the nearest minute. Anything above
 * zero but under a minute reads "<1m" rather than "0m": a sub-minute block is a
 * capture/generation quirk the reader needs to see, not time to round away. Zero is "0m".
 */
export function formatSeconds(seconds)
{
	const total = Number(seconds) || 0;

	if (total > 0 && total < 60) return "<1m";

	return formatMinutes(roundSecondsToMinutes(total));
}


export default { formatMinutes, roundSecondsToMinutes, formatSeconds };
